/* eslint-disable react-refresh/only-export-components */
/**
 * Shared building blocks for the Budget & Spend screens (build 2026-10-24).
 * Colours come from the theme (usePanelTokens / theme palette); no literal hex here.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, GlobalStyles, Skeleton, Slider, Stack, Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import { alpha } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { RingKpiCard, type RingKpiCardProps } from '@/components/panel';
import { usePanelTokens } from '@/components/panel/tokens';
import { budgetingApi, type CycleModel, type StepView, type VersionStatus } from '@/api/endpoints/budgeting';
import { extractErrorMessage, getCurrencySymbol } from '@/utils/format';

// ── Data hooks ────────────────────────────────────────────────────────────────

export const qk = {
  current: ['budget', 'cycle', 'current'] as const,
  model: (id: string) => ['budget', 'model', id] as const,
  version: (id: string) => ['budget', 'version', id] as const,
  spend: (year: number, dept?: string | null) => ['budget', 'spend', year, dept ?? 'all'] as const,
  pendingTransfers: (year: number) => ['budget', 'spend', 'pending-transfers', year] as const,
};

export function useBudgetCurrent() {
  return useQuery({ queryKey: qk.current, queryFn: budgetingApi.current, retry: false, staleTime: 60_000 });
}

/** Who heads what (department heads have no budget role; their scope is derived). */
export function useBudgetScope() {
  const q = useBudgetCurrent();
  return {
    loading: q.isLoading,
    viewer: q.data?.viewer,
    headOf: q.data?.viewer?.head_of ?? [],
    hasAccess: !!q.data,
  };
}

export function useCycleModel(cycleId?: string | null, enabled = true) {
  return useQuery({
    queryKey: qk.model(cycleId ?? 'none'),
    queryFn: () => budgetingApi.model(cycleId as string),
    enabled: !!cycleId && enabled,
  });
}

/** True when the viewer may read budget figures (the API's read set, or a department head).
 *  Staff with only budget.suggest / purchasing.request see the cycle but not its figures. */
export function readsBudget(v?: { is_finance: boolean; can_write: boolean; can_approve: boolean; can_admin: boolean; can_exec: boolean; head_of: string[] } | null) {
  return !!v && (v.is_finance || v.can_write || v.can_approve || v.can_admin || v.can_exec || v.head_of.length > 0);
}

/** The cycle being planned and its model, in one hook. */
export function usePlanningCycle() {
  const current = useBudgetCurrent();
  const cycleId = current.data?.cycle?.id ?? null;
  const viewer = current.data?.viewer;
  const model = useCycleModel(cycleId, readsBudget(viewer));
  return { current, cycleId, model, cycle: model.data?.cycle, viewer, reads: readsBudget(viewer) };
}

/** The year whose approved budget is in use (spend control): this calendar year. */
export const IN_USE_YEAR = new Date().getFullYear();

/**
 * useSearchParams for a Finance hub tab. The hub picks its tab from the URL hash (#budget.reports);
 * react-router's setSearchParams drops the hash and lands the user on Overview, so this setter
 * keeps it.
 */
export function useTabParams(): [URLSearchParams, (next: URLSearchParams) => void] {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const set = (next: URLSearchParams) =>
    navigate({ search: next.toString(), hash: location.hash }, { replace: true });
  return [params, set];
}

/**
 * One idempotency key per logical action: the same body gets the same key until the action
 * succeeds (call `done()`), so a retry after a timeout replays on the server instead of creating
 * a second record; a changed body gets a new key.
 */
export function useActionKey() {
  const ref = useRef<{ body: string; key: string } | null>(null);
  return {
    keyFor: (body: unknown) => {
      const b = JSON.stringify(body);
      if (!ref.current || ref.current.body !== b) ref.current = { body: b, key: crypto.randomUUID() };
      return ref.current.key;
    },
    done: () => { ref.current = null; },
  };
}

// ── Money and numbers ─────────────────────────────────────────────────────────

export function useMoney(currency: string | undefined) {
  return useMemo(() => {
    const ccy = currency || 'NGN';
    const sym = getCurrencySymbol(ccy);
    const dp = ccy === 'GHS' ? 2 : 1;
    const compact = (v: string | number | null | undefined): string => {
      if (v === null || v === undefined || v === '') return '—';
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      const sign = n < 0 ? '−' : '';
      const a = Math.abs(n);
      if (a >= 1e9) return `${sign}${sym}${(a / 1e9).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}bn`;
      if (a >= 1e6) return `${sign}${sym}${(a / 1e6).toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp })}m`;
      if (a >= 1e3) return `${sign}${sym}${(a / 1e3).toLocaleString('en-US', { maximumFractionDigits: 1 })}k`;
      return `${sign}${sym}${a.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
    };
    const full = (v: string | number | null | undefined): string => {
      if (v === null || v === undefined || v === '') return '—';
      const n = Number(v);
      if (Number.isNaN(n)) return '—';
      return `${n < 0 ? '−' : ''}${sym}${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };
    /** Millions with one decimal, no symbol — for dense grids. */
    const grid = (v: string | number | null | undefined): string => {
      if (v === null || v === undefined || v === '') return '';
      const n = Number(v) / 1e6;
      return n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: ccy === 'GHS' ? 3 : 1 });
    };
    return { sym, ccy, compact, full, grid, unit: ccy === 'GHS' ? 1e6 : 1e6 };
  }, [currency]);
}

export function pctOf(part: string | number, whole: string | number): number {
  const w = Number(whole);
  if (!w) return 0;
  return (Number(part) / w) * 100;
}

/** A typed figure (e.g. in millions) as the exact 2-decimal amount string the API takes. */
export function toAmount(v: string | number, unit: number): string {
  return (Math.round(Number(v) * unit * 100) / 100).toFixed(2);
}

export const n = (v: string | number | null | undefined) => (v === null || v === undefined || v === '' ? 0 : Number(v));

export function useMonths() {
  const { t } = useTranslation();
  return [
    t('bud.m.jan', 'Jan'), t('bud.m.feb', 'Feb'), t('bud.m.mar', 'Mar'), t('bud.m.apr', 'Apr'),
    t('bud.m.may', 'May'), t('bud.m.jun', 'Jun'), t('bud.m.jul', 'Jul'), t('bud.m.aug', 'Aug'),
    t('bud.m.sep', 'Sep'), t('bud.m.oct', 'Oct'), t('bud.m.nov', 'Nov'), t('bud.m.dec', 'Dec'),
  ];
}

export function fmtDate(iso: string | null | undefined, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', opts);
}

/** A calendar date stored as midnight UTC (a deadline): shown as that date in every time zone. */
export function fmtDay(iso: string | null | undefined) {
  return fmtDate(iso, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

// ── Layout pieces ─────────────────────────────────────────────────────────────

/** Phone width: every field, button and switch label on a budget screen is a 44 px target (verifier SH-11). */
const touchTargets = {
  '@media (max-width:599.95px)': {
    '.MuiInputBase-root:not(.MuiInputBase-multiline)': { minHeight: 44 },
    '.MuiButton-root, .MuiIconButton-root': { minHeight: 44, minWidth: 44 },
    '.MuiFormControlLabel-root': { minHeight: 44 },
  },
};

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <>
    <GlobalStyles styles={touchTargets} />
    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'flex-end' }} gap={1.5} sx={{ mb: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography component="h2" variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 900 }}>{subtitle}</Typography>}
      </Box>
      {actions && <Stack direction="row" gap={1} flexWrap="wrap" sx={{ flexShrink: 0 }}>{actions}</Stack>}
    </Stack>
    </>
  );
}

export function RingRow({ items }: { items: (RingKpiCardProps & { key: string })[] }) {
  const cols = items.length >= 5 ? 2.4 : 12 / Math.max(items.length, 1);
  return (
    <Grid container spacing={1.5} sx={{ mb: 2 }}>
      {items.map(({ key, ...it }) => (
        <Grid key={key} size={{ xs: 12, sm: 6, md: cols }}>
          <RingKpiCard {...it} />
        </Grid>
      ))}
    </Grid>
  );
}

export function Section({ title, sub, action, children, sx }: { title?: ReactNode; sub?: ReactNode; action?: ReactNode; children: ReactNode; sx?: Record<string, unknown> }) {
  const t = usePanelTokens();
  return (
    <Box sx={{ bgcolor: 'background.paper', border: `1px solid ${t.border}`, borderRadius: '12px', p: { xs: 2, md: 2.5 }, mb: 2, ...sx }}>
      {(title || action) && (
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1} sx={{ mb: sub ? 0.5 : 1.5 }}>
          {title && <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1rem' }}>{title}</Typography>}
          {action}
        </Stack>
      )}
      {sub && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{sub}</Typography>}
      {children}
    </Box>
  );
}

export function ChipGroup<T extends string>({ label, value, options, onChange, disabled }: {
  label?: ReactNode; value: T; options: { value: T; label: ReactNode }[]; onChange: (v: T) => void; disabled?: boolean;
}) {
  return (
    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" role="group" aria-label={typeof label === 'string' ? label : undefined}>
      {label && <Typography variant="body2" sx={{ fontWeight: 600, mr: 0.5 }}>{label}</Typography>}
      {options.map((o) => (
        <Chip
          key={o.value}
          label={o.label}
          clickable={!disabled}
          disabled={disabled && o.value !== value}
          color={o.value === value ? 'primary' : 'default'}
          variant={o.value === value ? 'filled' : 'outlined'}
          onClick={() => !disabled && onChange(o.value)}
          aria-pressed={o.value === value}
          sx={{ minHeight: 44, minWidth: 44, fontWeight: 600 }}
        />
      ))}
    </Stack>
  );
}

export type Tone = 'neutral' | 'good' | 'warning' | 'over' | 'info' | 'dark';

export function Pill({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  const t = usePanelTokens();
  const map: Record<Tone, { bg: string; fg: string }> = {
    neutral: { bg: t.track, fg: t.ink },
    good: { bg: t.positiveSoft, fg: t.positive },
    warning: { bg: alpha(t.amber, 0.16), fg: t.amberDark },
    over: { bg: alpha(t.negative, 0.12), fg: t.negative },
    info: { bg: t.aSoft, fg: t.a },
    dark: { bg: t.ink, fg: t.card },
  };
  const c = map[tone];
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', px: 1, py: 0.25, borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, bgcolor: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>
      {children}
    </Box>
  );
}

export function useStatusLabel() {
  const { t } = useTranslation();
  return (status: VersionStatus | string, step?: number | null, n?: number) => {
    switch (status) {
      case 'not_started': return { label: t('bud.status.not_started', 'Not started'), tone: 'over' as Tone };
      case 'draft': return { label: t('bud.status.draft', 'Draft'), tone: 'neutral' as Tone };
      case 'in_review': return { label: step && n ? t('bud.status.in_review_step', 'In review · step {{step}} of {{n}}', { step, n }) : t('bud.status.in_review', 'In review'), tone: 'info' as Tone };
      case 'sent_back': return { label: t('bud.status.sent_back', 'Sent back'), tone: 'warning' as Tone };
      case 'approved': return { label: t('bud.status.approved', 'Approved'), tone: 'good' as Tone };
      case 'locked': return { label: t('bud.status.locked', 'Locked'), tone: 'dark' as Tone };
      default: return { label: status, tone: 'neutral' as Tone };
    }
  };
}

/** Approval chain as coloured blocks: green approved, brand waiting, grey not reached. */
export function ChainBlocks({ steps, compact }: { steps: StepView[]; compact?: boolean }) {
  const t = usePanelTokens();
  const { t: tr } = useTranslation();
  return (
    <Stack direction="row" gap={0.5} aria-label={tr('bud.chain_aria', 'Approval chain')} role="list">
      {steps.map((s, i) => {
        const bg = s.status === 'approved' ? t.positive : s.status === 'pending' ? t.a : s.status === 'rejected' ? t.amber : t.track;
        return (
          <Box key={i} role="listitem" title={`${s.role}: ${s.status}`} aria-label={`${s.role}: ${s.status}`}
            sx={{ width: compact ? 18 : 28, height: 8, borderRadius: 4, bgcolor: bg }} />
        );
      })}
    </Stack>
  );
}

export function StepPills({ steps }: { steps: StepView[] }) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" gap={0.75} flexWrap="wrap">
      {steps.map((s, i) => (
        <Pill key={i} tone={s.status === 'approved' ? 'good' : s.status === 'pending' ? 'info' : s.status === 'rejected' ? 'warning' : 'neutral'}>
          {s.role}{s.status === 'pending' ? ` · ${t('bud.waiting', 'waiting')}` : ''}
        </Pill>
      ))}
    </Stack>
  );
}

export function SliderRow({ label, value, display, min, max, step, onCommit, disabled, ariaLabel }: {
  label: ReactNode; value: number; display: ReactNode; min: number; max: number; step: number;
  onCommit: (v: number) => void; disabled?: boolean; ariaLabel: string;
}) {
  // Keep the moved value until the server's value catches up, and commit once after the last
  // key press: committing every arrow press re-read the stale server value, so steps were lost.
  const [local, setLocal] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const settle = useRef<ReturnType<typeof setTimeout>>();
  // A change still waiting for its debounce is committed if the slider unmounts (tab switch).
  const pending = useRef<number | null>(null);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;
  useEffect(() => { setLocal(null); }, [value]);
  useEffect(() => () => {
    clearTimeout(timer.current); clearTimeout(settle.current);
    if (pending.current !== null) commitRef.current(pending.current);
  }, []);
  const v = local ?? value;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
        <Typography sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{local === null ? display : v}</Typography>
      </Stack>
      <Slider
        value={v} min={min} max={max} step={step} disabled={disabled}
        onChange={(_, x) => setLocal(x as number)}
        onChangeCommitted={(_, x) => {
          setLocal(x as number);
          clearTimeout(timer.current);
          clearTimeout(settle.current);
          pending.current = (x as number) === value ? null : (x as number);
          timer.current = setTimeout(() => {
            pending.current = null;
            if ((x as number) === value) { setLocal(null); return; }
            onCommit(x as number);
            settle.current = setTimeout(() => setLocal(null), 4000); // a refused change snaps back
          }, 350);
        }}
        aria-label={ariaLabel}
        sx={{ py: 1.5 }}
      />
    </Box>
  );
}

export function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <Box>
      {Array.from({ length: rows }).map((_, i) => <Skeleton key={i} variant="rounded" height={88} sx={{ mb: 1.5 }} />)}
    </Box>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  return <Alert severity="error" sx={{ mb: 2 }}>{extractErrorMessage(error)}</Alert>;
}

export function Empty({ title, children, action }: { title: ReactNode; children?: ReactNode; action?: ReactNode }) {
  return (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
      {children && <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 520, mx: 'auto' }}>{children}</Typography>}
      {action && <Box sx={{ mt: 2 }}>{action}</Box>}
    </Box>
  );
}

/** An AI note: shows the AI wording when available, otherwise the facts wording. The server derives
 *  every figure the model sees (the client only names the kind and, for a department, its version). */
export function AiNote({ title, cycleId, kind, versionId, fallback }: {
  title: ReactNode; cycleId: string | null | undefined; kind: string; versionId?: string; fallback: string[];
}) {
  const t = usePanelTokens();
  const { t: tr } = useTranslation();
  const q = useQuery({
    // The facts wording changes whenever the figures do, so it is part of the key: a note written for
    // yesterday's figures is never shown beside today's.
    queryKey: ['budget', 'narrative', cycleId, kind, versionId ?? null, fallback.join('\u0001')],
    queryFn: () => budgetingApi.narrative(cycleId as string, kind, versionId),
    enabled: !!cycleId,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const sentences = q.data?.ai && q.data.sentences?.length ? q.data.sentences : fallback;
  return (
    <Section
      title={<Stack direction="row" alignItems="center" gap={0.75}><AutoAwesomeOutlined sx={{ fontSize: 18, color: t.a }} />{title}</Stack>}
      action={q.isFetching ? <CircularProgress size={16} /> : (
        <Typography variant="caption" color="text.secondary">
          {q.data?.ai ? tr('bud.ai.by_ai', 'Written by AI from the figures shown') : tr('bud.ai.from_figures', 'From the figures shown')}
        </Typography>
      )}
    >
      <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'grid', gap: 1 }}>
        {sentences.map((s, i) => <Typography key={i} component="li" variant="body2">{s}</Typography>)}
      </Box>
    </Section>
  );
}

export function PrimaryAction(props: React.ComponentProps<typeof Button>) {
  return <Button variant="contained" disableElevation sx={{ minHeight: 44, fontWeight: 700, borderRadius: 2 }} {...props} />;
}

export function SecondaryAction(props: React.ComponentProps<typeof Button>) {
  return <Button variant="outlined" sx={{ minHeight: 44, fontWeight: 700, borderRadius: 2 }} {...props} />;
}

export type { CycleModel };

/** "FY2027 · Collecting and approving" beside the Finance title on Budget & Spend tabs. */
export function BudgetCyclePill() {
  const { t } = useTranslation();
  const location = useLocation();
  const current = useBudgetCurrent();
  const c = current.data?.cycle;
  if (!c || !/^#(budget\.|transactions\.purchases)/.test(location.hash)) return null;
  const stage: Record<string, string> = {
    setup: t('bud.pill.setup', 'Setting up'),
    targets_sent: t('bud.pill.targets_sent', 'Targets sent'),
    collecting: t('bud.pill.collecting', 'Collecting and approving'),
    review: t('bud.pill.review', 'In review'),
    approved: t('bud.pill.approved', 'Approved, ready to lock'),
    locked: t('bud.pill.locked', 'Locked · in use'),
    archived: t('bud.pill.archived', 'Archived'),
  };
  return <Pill tone={c.status === 'locked' ? 'good' : 'neutral'}>{t('bud.pill.label', 'FY{{year}} · {{stage}}', { year: c.fiscal_year, stage: stage[c.status] ?? c.status })}</Pill>;
}
